class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.trendingPlaceholder = this.querySelector('.quick-trending-placeholder');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.searchDetails = this.querySelector('.search_details');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');

    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.debounceTimer = null;

     // AJAX trending state
    this.trendingLoaded = false;
    this.trendingSectionUrl = '/?section_id=predictive-trending';

    this.setupEventListeners();
  }

   // Shared trending cache for all instances
  static trendingCache = null;
  static trendingLoading = false;

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.input.addEventListener('keyup', this.onKeyup.bind(this)); // KEYUP EVENT
    this.input.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));

    if (this.closeStickySearchButton) {
      this.closeStickySearchButton.addEventListener(
        'click',
        this.onCloseStickySearchClick.bind(this)
      );
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

 //Custom Search URL for `zh-hans` and `zh-hant` locales, as Shopify's Predictive Search does not support them.
getLocalePrefix() {
  const locale = window.Shopify?.locale || document.documentElement.lang || '';
  const path = window.location.pathname; 
  if (path.includes('zh-hans')) {
    return '/zh-hans'; 
  } else if (path.includes('zh-hant')) {
    return '/zh-hant'; 
  }


  return locale && locale !== 'en' ? `/${locale}` : '';
}

  onFocus() {
    const value = this.getQuery();

   if (!value.length) {
      this.loadTrendingViaAjax();
      return this.showTrendingAndProducts();
    }

    this.onChange();
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 100);
  }

  onKeyup(event) {
    this.onChange();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!newSearchTerm.length) {
      this.abortController.abort();
      this.searchTerm = '';
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.searchTerm = newSearchTerm;
      this.hideTrendingAndProducts();

      // Check if the language is supported by Shopify
      const isUnsupportedLocale = this.isUnsupportedLocale();

      if (isUnsupportedLocale) {
        console.log('Unsupported language detected. Switching to custom search...');
        this.handleCustomSearch(newSearchTerm);
      } else {
        this.getSearchResults(newSearchTerm);
      }
    }, 500);
  }

isUnsupportedLocale() {
  const locale = window.Shopify?.locale || document.documentElement.lang || '';
  const unsupportedLocales = ['ja', 'ar', 'ko', 'zh-CN', 'zh-TW', 'th'];
  return unsupportedLocales.includes(locale);
}

  /* Shopify's Predictive Search (only for supported languages) */
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    // Check if results are cached
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.updateViewAllLink(searchTerm);
      return;
    }

    this.abortController.abort();
    this.abortController = new AbortController();

    const shopifyPredictiveSearchURL = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=query,product,collection,page,article&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku`;

    fetch(shopifyPredictiveSearchURL, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((html) => {
        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = html;
        });
        this.cachedResults[queryKey] = html;
        this.renderSearchResults(html);
        this.updateViewAllLink(searchTerm);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.close();
      });
  }

  /*  Custom Search for unsupported languages */
 handleCustomSearch(searchTerm) {
  const queryKey = searchTerm.toLowerCase();
  const localePrefix = this.getLocalePrefix(); 

  const customSearchURL = `${localePrefix}/search?q=${encodeURIComponent(searchTerm)}&view=ajax`; 

  fetch(customSearchURL, { signal: this.abortController.signal })
    .then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text(); 
    })
    .then((html) => {
      this.allPredictiveSearchInstances.forEach((instance) => {
        instance.cachedResults[queryKey] = html;
      });

      this.cachedResults[queryKey] = html;
      this.renderSearchResults(html);
      this.updateViewAllLink(searchTerm);
    })
    .catch((error) => {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.close();
    });
}

  renderSearchResults(markup) {
    if (!markup || !markup.trim()) {
      this.showTrendingAndProducts();
      return;
    }

    this.predictiveSearchResults.innerHTML = markup;
    this.setAttribute('results', true);
    this.open();
    this.hideTrendingAndProducts();
    this.setLiveRegionResults();
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');

    if (!this.statusElement) return;

    this.statusElement.textContent = 'Loading';
    this.setAttribute('loading', true);
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
  }


  open() {
    this.predictiveSearchResults.style.maxHeight = `${window.innerHeight}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clear = false) {
    if (clear) this.input.value = '';
    this.removeAttribute('open');
    this.removeAttribute('results');
    this.input.setAttribute('aria-expanded', false);
    this.isOpen = false;
  }

  switchOption(direction) {
    const options = this.querySelectorAll('[role="option"]');
    if (!options.length) return;

    let index = [...options].findIndex(
      (el) => el.getAttribute('aria-selected') === 'true'
    );

    options.forEach(el => el.removeAttribute('aria-selected'));

    index = direction === 'up' ? index - 1 : index + 1;
    if (index < 0) index = options.length - 1;
    if (index >= options.length) index = 0;

    options[index].setAttribute('aria-selected', true);
  }

  selectOption() {
    const selected = this.querySelector('[aria-selected="true"] a');
    if (selected) selected.click();
  }

  /* UI helpers */
  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.remove('hidden');
    this.trendingAndProductsBlock.classList.add('is-show');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return;
    this.trendingAndProductsBlock.classList.add('hidden');
    this.trendingAndProductsBlock.classList.remove('is-show');
  }

   // Ajax for trending items
  loadTrendingViaAjax() {
    if (PredictiveSearch.trendingCache) {
      this.renderTrending(PredictiveSearch.trendingCache);
      return;
    }

    if (PredictiveSearch.trendingLoading) return;
    PredictiveSearch.trendingLoading = true;

    fetch(this.trendingSectionUrl)
      .then(res => res.text())
      .then(html => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-trending');
        if (!section) return;

        PredictiveSearch.trendingCache = section.innerHTML;

        document.querySelectorAll('predictive-search').forEach(instance => {
          instance.renderTrending(PredictiveSearch.trendingCache);
        });
      })
      .catch(err => console.error('Trending AJAX failed', err))
      .finally(() => PredictiveSearch.trendingLoading = false);
  }

   renderTrending(html) {
    if (!this.trendingPlaceholder) return;

    this.trendingPlaceholder.innerHTML = html;
    this.trendingAndProductsBlock = this.trendingPlaceholder.querySelector('[data-quick-trending-products]');
    this.trendingLoaded = true;
    this.showTrendingAndProducts();
  }

  onDocClick(e) {
    if (!this.contains(e.target)) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }  
 
  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  updateViewAllLink(searchTerm) {
    const link = document.querySelector('[data-qs-view-all-link]');
    if (!link) return;

    const localePrefix = this.getLocalePrefix();
    link.href = `${localePrefix}/search?q=${encodeURIComponent(searchTerm)}&type=product`;
  }

  onCloseStickySearchClick() {
    this.close(true);
    document.body.classList.remove('sticky-search-open');
  }
} 

customElements.define('predictive-search', PredictiveSearch);